// ============================================================================
// Header shell resources
// ============================================================================
// The Header is mounted above every authenticated surface, so its own reads
// cannot be attached to a route surface without making every route depend on
// that route's owner. These two resources are deliberately a separate shell
// registry: they use the same reviewed scope/key builder as route resources,
// but are neither route catalog entries nor evidence for a route release.
//
// There are exactly two independently settling resources here:
//   * the caller's open due/past-due task count;
//   * the notification badge plus dropdown list, which has always been one
//     atomic Promise.all so an incomplete bell never presents a partial list.
// ============================================================================

import {skipToken} from '@tanstack/react-query';

import {isInactiveAccount} from '../../lib/access.js';
import type {AuthState as AccessAuthState} from '../../lib/access.js';
import {countUnreadNotifications, loadRecentNotifications} from '../../lib/notificationsApi.js';
import {countMyOpenDueOrPastTasks} from '../../lib/tasksCenterApi.js';
import {UNRESOLVED_RESOURCE_KEY, resourceKey, scopeOrNull} from '../data/routeDataCatalog.js';
import type {AuthStateInput, AuthenticatedScope, QueryKey, ResourceParams} from '../data/routeDataCatalog.js';
import type {SupabaseClient} from '../../lib/supabaseTypes.js';

export const HEADER_SHELL_RESOURCE = Object.freeze({
  TASK_BADGE: 'shell.header.task-badge',
  NOTIFICATIONS: 'shell.header.notifications',
});

/**
 * The ordinary QueryScope remains the source of subject, effective-role, and
 * role-preview identity. `sessionEpoch` is deliberately a KEY PARAM rather
 * than a field added to QueryScope: its lifecycle belongs to the boot
 * coordinator, and route resources must retain their reviewed four-field
 * scope contract.
 *
 * The boot coordinator publishes a new positive epoch for every accepted
 * session. It is both a readiness boundary and a key parameter: zero, null,
 * malformed, unresolved, and signed-out states all remain inert, while a
 * same-subject sign-in receives a fresh cache key.
 */
export type HeaderShellScope = {
  readonly scope: AuthenticatedScope;
  readonly sessionEpoch: number;
};

export type HeaderShellResource<Data> = {
  readonly id: string;
  readonly key: (scope: HeaderShellScope, params?: ResourceParams) => QueryKey;
  readonly query: (client: SupabaseClient, params?: ResourceParams, signal?: AbortSignal) => Promise<Data>;
};

export type HeaderNotificationsData = {
  unread: number;
  recent: unknown[];
};

/**
 * Resolve the Header's authenticated shell scope without manufacturing a
 * public/signed-out namespace for private reads.
 */
export function headerShellScopeOrNull(
  authState: unknown,
  rolePreview: unknown = '',
  sessionEpoch: unknown,
): HeaderShellScope | null {
  const scope = scopeOrNull(authState as AuthStateInput, rolePreview);
  if (!scope || scope.kind !== 'authenticated') return null;
  // Migration 218: a deactivated account's Header starts no read at all -- the
  // server refuses both resources to it, and InactiveAccountScreen renders no
  // task or notification control to show them in.
  if (isInactiveAccount(authState as AccessAuthState)) return null;

  if (typeof sessionEpoch !== 'number' || !Number.isSafeInteger(sessionEpoch) || sessionEpoch <= 0) {
    return null;
  }

  return Object.freeze({scope, sessionEpoch});
}

/**
 * Reuse the route resource key builder verbatim. The boot epoch lives in
 * normalized params, so a same-subject sign-out/sign-in receives a fresh
 * cache entry without extending the reviewed QueryScope contract.
 */
export function headerShellResourceKey(
  resourceId: string,
  shellScope: HeaderShellScope,
  params: ResourceParams = {},
): QueryKey {
  return resourceKey(resourceId, shellScope.scope, {
    ...params,
    sessionEpoch: shellScope.sessionEpoch,
  });
}

function requiredStringParam(params: ResourceParams | undefined, name: string): string {
  const value = params?.[name];
  if (typeof value !== 'string' || !value) throw new Error(`Header shell resource requires ${name}`);
  return value;
}

function notificationLimit(params: ResourceParams | undefined): number {
  const value = params?.limit;
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : 20;
}

async function queryTaskBadge(
  client: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<number> {
  return countMyOpenDueOrPastTasks(
    client,
    requiredStringParam(params, 'subjectId'),
    requiredStringParam(params, 'today'),
    signal,
  );
}

async function queryNotifications(
  client: SupabaseClient,
  params: ResourceParams | undefined,
  signal?: AbortSignal,
): Promise<HeaderNotificationsData> {
  const subjectId = requiredStringParam(params, 'subjectId');
  const [unread, recent] = await Promise.all([
    countUnreadNotifications(client, subjectId, signal),
    loadRecentNotifications(client, {limit: notificationLimit(params), signal}),
  ]);
  return {unread: unread || 0, recent: recent || []};
}

const taskBadgeResource: HeaderShellResource<number> = Object.freeze({
  id: HEADER_SHELL_RESOURCE.TASK_BADGE,
  key: (scope, params = {}) => headerShellResourceKey(HEADER_SHELL_RESOURCE.TASK_BADGE, scope, params),
  query: queryTaskBadge,
});

const notificationsResource: HeaderShellResource<HeaderNotificationsData> = Object.freeze({
  id: HEADER_SHELL_RESOURCE.NOTIFICATIONS,
  key: (scope, params = {}) => headerShellResourceKey(HEADER_SHELL_RESOURCE.NOTIFICATIONS, scope, params),
  query: queryNotifications,
});

export const HEADER_SHELL_RESOURCES = Object.freeze({
  TASK_BADGE: taskBadgeResource,
  NOTIFICATIONS: notificationsResource,
});

export type HeaderShellQueryInput<Data> = {
  resource: HeaderShellResource<Data>;
  scope: HeaderShellScope | null;
  params?: ResourceParams;
  enabled?: boolean;
  client: SupabaseClient;
};

export type HeaderShellQueryOptions<Data> = {
  queryKey: readonly unknown[];
  queryFn: typeof skipToken | ((context: {signal: AbortSignal}) => Promise<Data>);
  enabled: boolean;
};

/**
 * The small query seam shared by both shell resources. It mirrors the route
 * runtime's inert-key contract: an unresolved/signed-out Header has no
 * runnable function, not merely a disabled request under an invented key.
 */
export function headerShellQueryOptions<Data>({
  resource,
  scope,
  params,
  enabled = true,
  client,
}: HeaderShellQueryInput<Data>): HeaderShellQueryOptions<Data> {
  if (!resource || typeof resource.key !== 'function' || typeof resource.query !== 'function') {
    throw new Error('headerShellQueryOptions: a shell resource with key() and query() is required');
  }
  if (!scope) {
    return {queryKey: UNRESOLVED_RESOURCE_KEY, queryFn: skipToken, enabled: false};
  }
  return {
    queryKey: resource.key(scope, params),
    queryFn: ({signal}) => resource.query(client, params, signal),
    enabled: Boolean(enabled),
  };
}
